import React, { useState } from "react";
import { Search, Loader2, Stethoscope, Calendar, AlertTriangle, Lightbulb, MessageSquare, Sparkles, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/Primitives";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import Icd10Autocomplete from "@/components/Icd10Autocomplete";

const DEFAULT_FORM = {
  patient_age: "",
  patient_sex: "F",
  chief_complaint: "",
  problem_list: "",
  current_meds: "",
  appointment_type: "follow-up",
  recent_labs: "",
};

export default function EncounterPrep() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const run = async (e) => {
    e?.preventDefault();
    if (!form.chief_complaint.trim()) { toast.error("Chief complaint is required"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/encounter-prep/generate", {
        ...form,
        patient_age: form.patient_age ? Number(form.patient_age) : null,
      });
      setBrief(data);
      // Log estimated time saved (target: 8 minutes of pre-appointment reading)
      api.post("/time-saved/log", { module: "encounter-prep", minutes_saved: 8 }).catch(() => {});
      toast.success("Encounter brief ready");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't generate brief");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="encounter-prep-page" className="max-w-6xl mx-auto pb-12">
      <PageHeader
        eyebrow="Pre-visit intelligence"
        title="Encounter Prep"
        subtitle="A 90-second AI-generated brief before you walk in. Anticipate what the patient may bring up — including web/AI-sourced concerns — so the visit moves faster and feels more informed."
        testid="encounter-prep-header"
      />

      <div className="grid lg:grid-cols-[440px_1fr] gap-6">
        {/* LEFT — input form */}
        <SectionCard title="Patient context" subtitle="No PHI is stored — this brief is for this session only" testid="encounter-prep-form">
          <form onSubmit={run} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <FormField label="Age">
                <input value={form.patient_age} onChange={update("patient_age")} type="number" min="0" max="120"
                  className="input" style={{ minHeight: 44, fontSize: 16 }} data-testid="ep-age" />
              </FormField>
              <FormField label="Sex">
                <select value={form.patient_sex} onChange={update("patient_sex")} className="input" style={{ minHeight: 44, fontSize: 16 }} data-testid="ep-sex">
                  <option value="F">Female</option><option value="M">Male</option><option value="X">Other</option>
                </select>
              </FormField>
            </div>
            <FormField label="Chief complaint *">
              <input value={form.chief_complaint} onChange={update("chief_complaint")} required
                placeholder="e.g. recurring chest tightness on exertion"
                className="input" style={{ minHeight: 44, fontSize: 16 }} data-testid="ep-chief" />
            </FormField>
            <FormField label="Appointment type">
              <select value={form.appointment_type} onChange={update("appointment_type")} className="input" style={{ minHeight: 44, fontSize: 16 }} data-testid="ep-apt">
                <option value="new">New patient consult</option>
                <option value="follow-up">Follow-up</option>
                <option value="annual">Annual physical</option>
                <option value="urgent">Urgent / acute</option>
                <option value="pre-op">Pre-op clearance</option>
                <option value="telemedicine">Telemedicine</option>
              </select>
            </FormField>
            <FormField label="Problem list (ICD-10 autocomplete)">
              <Icd10Autocomplete
                value={form.problem_list}
                onChange={(v) => setForm((f) => ({ ...f, problem_list: v }))}
                placeholder="Search code or condition (e.g. I10 or hypertension)"
                testid="ep-problems-icd"
              />
              <textarea value={form.problem_list} onChange={update("problem_list")} rows={2}
                placeholder="Or free-text: HTN, T2DM, hyperlipidemia"
                className="input mt-2" style={{ fontSize: 15 }} data-testid="ep-problems" />
            </FormField>
            <FormField label="Current medications">
              <textarea value={form.current_meds} onChange={update("current_meds")} rows={2}
                placeholder="metformin 1000 mg BID, lisinopril 20 mg daily"
                className="input" style={{ fontSize: 15 }} data-testid="ep-meds" />
            </FormField>
            <FormField label="Recent labs / imaging">
              <textarea value={form.recent_labs} onChange={update("recent_labs")} rows={2}
                placeholder="A1c 7.4 (3 mo ago), LDL 142, CMP normal"
                className="input" style={{ fontSize: 15 }} data-testid="ep-labs" />
            </FormField>
            <button type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-brand-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40"
              style={{ minHeight: 48 }}
              data-testid="ep-generate">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {loading ? "Generating brief…" : "Generate encounter brief"}
            </button>
            <div className="text-[10px] italic text-brand-muted leading-relaxed flex items-start gap-1">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              Designed for the 34% of patients who arrive with AI-sourced research. Your brief addresses what they may ask about before they ask.
            </div>
          </form>
        </SectionCard>

        {/* RIGHT — generated brief */}
        <div className="space-y-5">
          {!brief && !loading && (
            <div className="section-card text-center py-16 text-sm text-brand-muted" data-testid="ep-empty">
              <Stethoscope className="w-10 h-10 mx-auto mb-3 opacity-30" />
              Enter the patient context on the left and tap <b>Generate</b>.<br />
              Your AI brief will land here in ~10 seconds.
            </div>
          )}
          {loading && (
            <div className="section-card text-center py-16 text-sm text-brand-muted" data-testid="ep-loading">
              <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin text-brand-primary" />
              Synthesizing differential, anticipated questions, talking points…
            </div>
          )}
          {brief && (
            <>
              <SectionCard
                title="At-a-glance"
                subtitle="60-second read before walking in"
                testid="brief-glance"
                actions={
                  <ConfidenceBadge
                    compact
                    confidence={brief.confidence?.level || "moderate"}
                    evidenceClass={brief.confidence?.evidence_class || null}
                    sources={brief.confidence?.sources || []}
                    modelRef={brief.confidence?.model || "Claude Sonnet 4.5"}
                    testid="brief-confidence"
                  />
                }
              >
                <BriefBlock icon={Calendar} title="Visit context">{brief.context_summary}</BriefBlock>
              </SectionCard>

              <SectionCard title="Likely differentials & framing" testid="brief-differentials">
                <BriefBlock icon={Stethoscope} title="Differential considerations">
                  <ul className="space-y-1 text-sm">
                    {(brief.differentials || []).map((d, i) => (
                      <li key={i} className="flex gap-2" data-testid={`brief-diff-${i}`}>
                        <span className="text-brand-primary font-bold">{i + 1}.</span>
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </BriefBlock>
                {brief.red_flags?.length > 0 && (
                  <BriefBlock icon={AlertTriangle} title="Red flags to rule out" tone="warn">
                    <ul className="space-y-1 text-sm">
                      {brief.red_flags.map((f, i) => (
                        <li key={i} className="flex gap-2 text-amber-900" data-testid={`brief-red-${i}`}>
                          <span className="text-amber-700">⚠</span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </BriefBlock>
                )}
              </SectionCard>

              <SectionCard title="What the patient may bring up" subtitle="Pre-empt the AI/web concerns" testid="brief-questions">
                <BriefBlock icon={MessageSquare} title="Anticipated questions">
                  <ul className="space-y-2 text-sm">
                    {(brief.anticipated_questions || []).map((q, i) => (
                      <li key={i} className="rounded-md bg-brand-bg p-3" data-testid={`brief-q-${i}`}>
                        <div className="text-brand-text font-medium">{q.question}</div>
                        {q.suggested_response && (
                          <div className="text-xs text-brand-muted mt-1 leading-relaxed">
                            <span className="font-semibold text-brand-primary uppercase text-[9px] tracking-wider">Response framing:</span> {q.suggested_response}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </BriefBlock>
              </SectionCard>

              <SectionCard title="Talking points & next steps" testid="brief-plan">
                <BriefBlock icon={Lightbulb} title="Visit talking points">
                  <ul className="space-y-1 text-sm list-disc pl-5">
                    {(brief.talking_points || []).map((t, i) => <li key={i} data-testid={`brief-tp-${i}`}>{t}</li>)}
                  </ul>
                </BriefBlock>
                {brief.suggested_orders?.length > 0 && (
                  <BriefBlock icon={Sparkles} title="Suggested orders / workup">
                    <ul className="space-y-1 text-sm list-disc pl-5">
                      {brief.suggested_orders.map((o, i) => <li key={i} data-testid={`brief-order-${i}`}>{o}</li>)}
                    </ul>
                  </BriefBlock>
                )}
              </SectionCard>

              <div className="text-[10px] italic text-brand-muted text-center px-4">
                {brief.disclaimer || "AI-generated brief. Verify against patient history, exam, and clinical judgement."}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const FormField = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-semibold uppercase tracking-wide text-brand-muted mb-1 block">{label}</span>
    {children}
  </label>
);

const BriefBlock = ({ icon: Icon, title, children, tone }) => (
  <div className="mb-4 last:mb-0">
    <div className={`flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold mb-2 ${tone === "warn" ? "text-amber-700" : "text-brand-muted"}`}>
      <Icon className="w-3 h-3" /> {title}
    </div>
    <div className="text-sm text-brand-text leading-relaxed">{children}</div>
  </div>
);
